import { auditReports } from '@mi-front/api-clients';
import { DateTimeStr } from '@mi-front/core';
import {
  OptionType,
  StationFilterOption,
} from '../../../../../../../../components/stations/advanced-filter/types';
import { GipReportValues } from '../form/types';
import { MinimumStationsGroup, Station, StationType } from '@app/core/types/stations';

// Helper function to get station type label
const getStationTypeLabel = (type: StationType): string => {
  const labels = {
    [StationType.STANDALONE]: 'Standalone',
    [StationType.HEADER]: 'Header',
    [StationType.REPEATER]: 'Repeater',
    [StationType.AFFILIATE]: 'Affiliate',
  };
  return labels[type] || type;
};

/**
 * Converts GIP report API values back to form values
 */
export function mapGipReportDataDtoToValues(
  apiValues: auditReports.GipReportDataDto,
  allStations: readonly Station[],
  stationGroups: readonly MinimumStationsGroup[] = [],
): GipReportValues {
  // Find custom field key and value if they exist
  let customFieldKey: { name: string } | null = null;
  let customFieldValue = '';

  if (apiValues.customFields && Object.keys(apiValues.customFields).length > 0) {
    const [key] = Object.keys(apiValues.customFields);
    customFieldKey = { name: key };
    customFieldValue = apiValues.customFields[key] || '';
  }

  // Handle station selection
  const { stationsSelectionType, stations, stationsFilter } = (() => {
    if (apiValues.stationsFilters) {
      // Advanced filtering
      const stationsFilter: StationFilterOption[] = apiValues.stationsFilters
        .map((filter) => {
          switch (filter.type) {
            case auditReports.StationsFiltersTypeEnum.STATION_TYPE:
              return {
                type: OptionType.StationType,
                value: filter.value as StationType,
                label: getStationTypeLabel(filter.value as StationType),
              } as StationFilterOption;

            case auditReports.StationsFiltersTypeEnum2.STATION_GROUP: {
              const group = stationGroups.find((g) => g.id === filter.value);
              if (!group) return null;
              return {
                type: OptionType.StationGroup,
                value: group,
                label: group.name,
              } as StationFilterOption;
            }

            case auditReports.StationsFiltersTypeEnum3.STATION_INDIVIDUAL: {
              const station = allStations.find((s) => s.id === filter.value);
              if (!station) return null;
              return {
                type: OptionType.IndividualStation,
                value: station,
                label: station.name,
              } as StationFilterOption;
            }

            default:
              throw new Error(`Unknown station filter type: ${JSON.stringify(filter)}`);
          }
        })
        .filter((filter): filter is StationFilterOption => filter !== null);

      return {
        stationsSelectionType: 'advanced' as const,
        stations: [],
        stationsFilter,
      };
    }

    if (apiValues.stationsIds?.length) {
      // Simple station selection
      return {
        stationsSelectionType: 'simple' as const,
        stations: allStations.filter((s) => apiValues.stationsIds!.includes(s.id)),
        stationsFilter: [],
      };
    }

    // No stations specified, use all stations
    return {
      stationsSelectionType: 'simple' as const,
      stations: [...allStations],
      stationsFilter: [],
    };
  })();

  return {
    stationsSelectionType,
    stations,
    stationsFilter,
    from: apiValues.from as DateTimeStr,
    to: apiValues.to as DateTimeStr,
    campaigns: (apiValues.campaigns || []).map((name) => ({ name })),
    advertisers: (apiValues.advertisers || []).map((name) => ({ name })),
    versions: (apiValues.versions || []).map((name) => ({ name })),
    customFieldKey,
    customFieldValue,
    customIds: (apiValues.customIds || []).map((customId) => ({ customId })),
    orders: apiValues.orders || [],
    excludedMatches: apiValues.excludedMatches || [],
  };
}
